import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { initializePaddle, Paddle } from '@paddle/paddle-js';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

interface CheckoutOptions {
  organizationId?: string;
  email?: string;
}

interface PaddleContextType {
  paddle: Paddle | null;
  isLoading: boolean;
  error: string | null;
  openCheckout: (priceId: string, opts?: CheckoutOptions) => void;
}

const PaddleContext = createContext<PaddleContextType | undefined>(undefined);

// Hook exported separately to satisfy react-refresh
function usePaddleHook() {
  const context = useContext(PaddleContext);
  if (!context) {
    throw new Error('usePaddle must be used within a PaddleProvider');
  }
  return context;
}

interface PaddleProviderProps {
  children: ReactNode;
}

export const PaddleProvider: React.FC<PaddleProviderProps> = ({ children }) => {
  const [paddle, setPaddle] = useState<Paddle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentOrganization, loading: authLoading } = useAuth();
  const initialized = useRef(false);

  useEffect(() => {
    // Paddle.Initialize can only run once per page load, and pwCustomer must
    // be set at that call — so wait for auth to resolve and, if there's an
    // organization, look up its Paddle customer ID first for Retain.
    if (initialized.current || authLoading) return;
    initialized.current = true;

    const initPaddle = async () => {
      try {
        const clientToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;

        if (!clientToken) {
          console.warn('Paddle client token not configured');
          setError('Paddle client token not configured');
          setIsLoading(false);
          return;
        }

        let pwCustomerId: string | undefined;
        if (currentOrganization) {
          try {
            const usage = await api.getBillingStatus(currentOrganization.id);
            pwCustomerId = usage.paddle_customer_id || undefined;
          } catch (err) {
            console.warn('Could not fetch Paddle customer ID for Retain:', err);
          }
        }

        const paddleInstance = await initializePaddle({
          token: clientToken,
          pwCustomer: pwCustomerId ? { id: pwCustomerId } : undefined,
          checkout: {
            settings: {
              displayMode: 'overlay',
              theme: 'dark',
            },
          },
          eventCallback: (event) => {
            // Handle Paddle events - log full details for debugging
            if (event.name === 'checkout.completed') {
              // The webhook will handle the subscription creation
            } else if (event.name === 'checkout.closed') {
            } else if (event.name === 'checkout.error') {
              console.error('Checkout error - full event:', JSON.stringify(event, null, 2));
            } else if (event.name === 'checkout.loaded') {
            }
          },
        });

        setPaddle(paddleInstance ?? null);
      } catch (err) {
        console.error('Failed to initialize Paddle:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize Paddle');
      } finally {
        setIsLoading(false);
      }
    };

    initPaddle();
  }, [authLoading, currentOrganization]);

  const openCheckout = (priceId: string, opts?: CheckoutOptions) => {
    if (paddle) {
      paddle.Checkout.open({
        items: [{ priceId, quantity: 1 }],
        customer: opts?.email ? { email: opts.email } : undefined,
        customData: opts?.organizationId ? { organization_id: opts.organizationId } : undefined,
      });
    } else {
      console.error('Paddle not initialized');
    }
  };

  return (
    <PaddleContext.Provider value={{ paddle, isLoading, error, openCheckout }}>
      {children}
    </PaddleContext.Provider>
  );
};

export { usePaddleHook as usePaddle };
export default PaddleContext;
